import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Dataset, DatasetFilters } from "../../types/dataset";
import { useAuth } from "../auth/AuthContext";
import { CreateDatasetDialog } from "./components/CreateDatasetDialog";
import { DeleteConfirmationDialog } from "./components/DeleteConfirmationDialog";
import { useDatasets } from "./hooks/useDatasets";

export const DatasetManagement: React.FC = () => {
  const { tenantId } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState<DatasetFilters>({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [datasetToDelete, setDatasetToDelete] = useState<Dataset | null>(null);

  // Memoize pagination to prevent unnecessary re-renders
  const paginationParams = useMemo(() => ({
    page: page + 1,
    limit: rowsPerPage
  }), [page, rowsPerPage]);

  const {
    datasets,
    paginatedData,
    loading,
    error,
    createDataset,
    deleteDataset
  } = useDatasets({
    pagination: paginationParams,
    filters
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: event.target.value || undefined }));
    setPage(0); // Reset to first page when searching
  };

  const clearSearch = () => {
    setFilters(prev => ({ ...prev, search: undefined }));
    setPage(0);
  };

  const handleCreateDataset = async (name: string, description: string) => {
    const result = await createDataset({ dataset_name: name, dataset_description: description, tenant_id: tenantId || "" });
    if (result) {
      setCreateDialogOpen(false);
    }
  };

  const handleDeleteClick = (dataset: Dataset) => {
    setDatasetToDelete(dataset);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (datasetToDelete) {
      const success = await deleteDataset(datasetToDelete.id);
      if (success) {
        setDeleteDialogOpen(false);
        setDatasetToDelete(null);
      }
    }
  };

  const handleRowClick = (dataset: Dataset) => {
    navigate(`/dataset/${dataset.id}`);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" component="h1">
          Dataset Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          disabled={loading}
        >
          Create Dataset
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="Search datasets..."
            value={filters.search || ""}
            onChange={handleSearchChange}
            variant="outlined"
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />,
              endAdornment: filters.search && (
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon />
                </IconButton>
              )
            }}
          />
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && datasets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : datasets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No datasets found. Create your first dataset to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                datasets.map((dataset) => (
                  <TableRow
                    key={dataset.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(dataset)}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">{dataset.dataset_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {dataset.dataset_description}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={dataset.dataset_item_count}
                        size="small"
                        color={dataset.dataset_item_count > 0 ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>{dataset.created_by}</TableCell>
                    <TableCell>{formatDate(dataset.created_at)}</TableCell>
                    <TableCell>{formatDate(dataset.updated_at)}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Delete dataset">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(dataset)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginatedData && (
          <TablePagination
            component="div"
            count={paginatedData.total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 20, 50]}
          />
        )}
      </Paper>

      <CreateDatasetDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onConfirm={handleCreateDataset}
        loading={loading}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        dataset={datasetToDelete}
        loading={loading}
      />
    </Box>
  );
};
